import { useApolloClient, useLazyQuery } from '@apollo/client';
import { AccessForbiddenPage } from '@openx/components-core/lib/AccessForbiddenBackPage/AccessForbiddenPage';
import { useQueryClient } from '@tanstack/react-query';
import type { User as FBUser } from 'firebase/auth';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { SKIP_AUTH } from '../../config';
import { useFirebaseListener } from '../../firebaseIntegration/useFirebaseListener';
import {
  confirmPasswordReset,
  fetchAuthToken,
  login,
  logout,
  sendPasswordResetEmail,
  verifyPasswordResetCode,
} from '../../firebaseIntegration/utils';
import { CheckUserPermissionsDocument } from '../../graphql/query/CheckUserPermissions.generated';
import { SessionDocument } from '../../graphql/query/GetSession.generated';
import { FEDERATED_QUERY_KEY } from '../../modules/graphql/federatedGraphQLRequest';
import { isAdmin } from '../../permissions/validators';
import { type AuthContext, type AuthProviderProps, authContext } from './context';
import { mapOfAuthStateToAction } from './mapOfAuthStateToAction';
import { AuthState, type Session } from './types';

const publicPages = ['/login', '/forgot-password', '/set-password', '/terms'];
const privateRoutes = ['/users'];

export function AuthProvider({ children, auth }: AuthProviderProps) {
  const firebaseListener = useFirebaseListener(auth);
  const apolloClient = useApolloClient();
  const queryClient = useQueryClient();

  const location = useLocation();
  const navigate = useNavigate();

  const [authState, setAuthState] = useState<AuthState>(AuthState.UNAUTHENTICATED);

  const pageIsPublic = publicPages.includes(location.pathname);
  const routeIsPrivate = privateRoutes.some(route => location.pathname.includes(route));

  const [triggerCheckPermissionsQuery, checkPermissionQuery] = useLazyQuery(CheckUserPermissionsDocument);
  const [triggerSessionQuery, sessionQuery] = useLazyQuery(SessionDocument, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const sessionContextValue = useMemo<Session | null>(() => {
    if (authState === AuthState.UNAUTHENTICATED || !sessionQuery?.data?.getSession) {
      return null;
    }

    const sessionValue = structuredClone(sessionQuery.data.getSession);

    return { ...sessionValue, fullName: `${sessionValue.first_name} ${sessionValue.last_name}` };
  }, [sessionQuery.data, authState]);

  const { user: currentUser } = firebaseListener;

  const handleLogin = useCallback(
    async (email, password) => {
      await apolloClient.clearStore();
      // Federated-API results are per-user, so none may outlive the previous user's session
      queryClient.removeQueries({ queryKey: [FEDERATED_QUERY_KEY] });
      return login({ auth, email, password });
    },
    [apolloClient, queryClient, auth]
  );

  const handleLogout = useCallback(() => logout({ auth }), [auth]);

  useEffect(() => {
    mapOfAuthStateToAction[authState]({
      auth,
      checkPermissionQuery,
      currentPage: window.location.pathname,
      fetchAuthToken,
      firebaseListener,
      location,
      logout: handleLogout,
      navigate,
      pageIsPublic,
      sessionContextValue,
      sessionQuery,
      setAuthState,
      skipAuth: !!SKIP_AUTH,
      triggerCheckPermissionsQuery,
      triggerSessionQuery,
    });
  }, [
    authState,
    auth,
    checkPermissionQuery,
    pageIsPublic,
    sessionContextValue,
    sessionQuery,
    triggerCheckPermissionsQuery,
    triggerSessionQuery,
    firebaseListener,
    location,
    navigate,
    handleLogout,
  ]);

  const isAuthenticating = useMemo(() => {
    if (authState === AuthState.SESSION_OBTAINED || authState === AuthState.UNAUTHENTICATED) return false;

    return true;
  }, [authState]);

  const value: AuthContext = useMemo(
    () => ({
      confirmPasswordReset: (oobCode, newPassword) => confirmPasswordReset({ auth, newPassword, oobCode }),
      currentUser: SKIP_AUTH ? ({ getIdToken: () => null } as unknown as FBUser) : currentUser,
      loading: firebaseListener.loading || isAuthenticating,
      sendPasswordResetEmail: (email, url) => sendPasswordResetEmail({ auth, email, url }),
      session: sessionContextValue,
      signInWithEmailAndPassword: handleLogin,
      signOut: handleLogout,
      verifyPasswordResetCode: code => verifyPasswordResetCode({ auth, code }),
    }),
    [isAuthenticating, currentUser, firebaseListener.loading, sessionContextValue, handleLogin, handleLogout, auth]
  );

  const isAllowed = useMemo(() => {
    if (routeIsPrivate && authState === AuthState.SESSION_OBTAINED) {
      return isAdmin(value.session);
    }

    return true;
  }, [authState, routeIsPrivate, value.session]);

  const renderChildren = useCallback(() => {
    if (value.loading) return null;

    if (!isAllowed) return <AccessForbiddenPage />;

    return children;
  }, [isAllowed, value.loading, children]);

  return <authContext.Provider value={value}>{renderChildren()}</authContext.Provider>;
}
